import {gql} from '@apollo/client';
import React from 'react';

import {assert} from 'Asserts';
import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {assertIsAccessible, isInaccessible} from 'graphql-client/InaccessibleInfo';
import NoFlowchartViewer from 'processes/graph-builder-processes/flowchart-viz/NoFlowchartViewer';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  ProcessTaskFlowchartRootTaskQuery,
  ProcessTaskFlowchartRootTaskQueryVariables,
} from './__generated/ProcessTaskFlowchartRootTaskQuery';
import LiveUpdatingTaskGroupFlowchartWrapper from './LiveUpdatingTaskGroupFlowchartWrapper';
import ProcessStaticFlowchartViewer from './ProcessStaticFlowchartViewer';
import TaskGroupFlowchartExecutionViewer from './TaskGroupFlowchartExecutionViewer';
import buildStaticFlowchartStructureFromGql from './util/buildStaticFlowchartStructureFromGql';
import {mergeTaskGroupLineageIntoFlowchart} from './util/mergeTaskGroupLineageIntoFlowchart';

export const PROCESS_TASK_FLOWCHART_ROOT_TASK_QUERY = gql`
  query ProcessTaskFlowchartRootTaskQuery($rootTaskId: StrictString!, $activeTaskId: StrictString!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    rootTask: processTask(id: $rootTaskId) {
      ... on ProcessTaskNode {
        id
        name
        taskSchema {
          ... on ProcessTaskSchemaNode {
            id
            name
            clientOrigin
            outputSchema {
              id
            }
          }
          ... on InaccessibleInfoNode {
            ...InaccessibleInfoGraphQL
          }
        }
        executionFlowchart {
          id
          numNodes
        }
        process {
          ... on ProcessNode {
            id
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
    activeTask: processTask(id: $activeTaskId) {
      ... on ProcessTaskNode {
        id
        process {
          ... on ProcessNode {
            id
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function ProcessTaskFlowchartQueryingViewer(props: {taskId: string; rootTaskId: string}): JSX.Element {
  return (
    <BenchlingQuery<ProcessTaskFlowchartRootTaskQuery, ProcessTaskFlowchartRootTaskQueryVariables>
      query={PROCESS_TASK_FLOWCHART_ROOT_TASK_QUERY}
      variables={{rootTaskId: props.rootTaskId, activeTaskId: props.taskId}}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {(data: ProcessTaskFlowchartRootTaskQuery, flowchartTaskQuery) => {
        const rootTask = data.rootTask;
        const activeTask = data.activeTask;
        if (isInaccessible(rootTask)) {
          return (
            <WorkspaceItemInaccessibleGuard
              inaccessibleInfo={rootTask}
              modelType={ModelTypeConstants.PROCESS_TASK}
            />
          );
        }
        const rootProcess = rootTask.process;
        if (isInaccessible(rootTask.taskSchema)) {
          return <NoFlowchartViewer message="The flowchart is not accessible" />;
        }
        const flowchart = rootTask.executionFlowchart;
        assert(flowchart != null, 'Expected execution flowchart in root task to not be null or undefined', {
          rootTask,
        });

        if (flowchart.numNodes > BenchlingConfig.WORKFLOWS_MAX_NODES_PER_FLOWCHART_CAN_BE_RENDERED_IN_UI) {
          return <NoFlowchartViewer message="The flowchart is too large to display" />;
        }

        return (
          <LiveUpdatingTaskGroupFlowchartWrapper
            parentId={rootProcess.id}
            refetch={flowchartTaskQuery.refetch}
            flowchartIds={[flowchart.id]}
            processId={rootProcess.id}
            rootTaskId={props.rootTaskId}
          >
            {(flowcharts, flowchartData, forceRefetch) => {
              if (flowcharts.length === 0 || isInaccessible(flowchartData.process)) {
                return <NoFlowchartViewer message="The flowchart is not accessible" />;
              }

              if (BenchlingConfig.WORKFLOWS_IMPROVED_ACM_PCM_EDITOR) {
                return (
                  <TaskGroupFlowchartExecutionViewer
                    key={rootProcess.id}
                    flowchartProcessId={rootProcess.id}
                    flowchartTitle={rootTask.name}
                    rootProcessTaskSchemaId={assertIsAccessible(rootTask.taskSchema).id}
                    sourceType="task_details_tab"
                    canEditFlowchart={false}
                    editDisabledMessage={null}
                    onFlowchartModified={forceRefetch}
                    isFlowchartMappingInProgress={
                      flowchartData.process.isMappingInProgressForFlowchartRootedAtProcess
                    }
                    actionOrigin={ActionOrigin.WORKFLOWS_TASK_DETAILS_PAGE}
                  />
                );
              }

              const newFlowchart = buildStaticFlowchartStructureFromGql({
                flowchartStructure: flowcharts[0],
                schema: assertIsAccessible(rootTask.taskSchema),
                nodeAnalytics: {
                  rootProcessId: rootProcess.id,
                },
                metadata: {
                  title: rootTask.name,
                },
              });

              const finalFlowchart = mergeTaskGroupLineageIntoFlowchart({
                flowchart: newFlowchart,
                rootProcess: flowchartData.process,
                clientOrigin: assertIsAccessible(rootTask.taskSchema).clientOrigin,
                processBeingViewed: !isInaccessible(activeTask) ? activeTask.process.id : undefined,
                shouldLinkProcess: false,
              });

              return (
                <ProcessStaticFlowchartViewer
                  flowchart={finalFlowchart}
                  actionOrigin={ActionOrigin.WORKFLOWS_TASK_GROUP_PAGE}
                  isFlowchartMappingInProgress={
                    flowchartData.process.isMappingInProgressForFlowchartRootedAtProcess
                  }
                />
              );
            }}
          </LiveUpdatingTaskGroupFlowchartWrapper>
        );
      }}
    </BenchlingQuery>
  );
}

export default ProcessTaskFlowchartQueryingViewer;
